/**
 * TEMP-H-002: Control Queries Hook
 * 
 * Fetches execution control state and subscribes to real-time updates.
 * Integrates WebSocket events with React Query cache for live metrics.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { 
  ControlQueries
} from '@/lib/control-api-types';
import { controlKeys } from '@/lib/control-api-types';
import { joinApiPath } from '@/lib/api-config';
import { getApiHeaders } from '@/lib/api-base';
import { getWebSocketClient } from '@/lib/websocket-enhanced';

/**
 * Hook to fetch and subscribe to execution control queries
 */
export function useControlQueries(executionId: string) {
  const queryClient = useQueryClient();
  const websocketHealthy = useRef(false);

  // Fetch control queries from API
  const query = useQuery({
    queryKey: controlKeys.queries(executionId),
    queryFn: async (): Promise<ControlQueries> => {
      const response = await fetch(joinApiPath('executions', executionId, 'queries'), {
        credentials: 'include',
        headers: getApiHeaders(),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ 
          message: `HTTP ${response.status}: ${response.statusText}` 
        }));
        
        // Enhanced error context with auth semantics
        let errorMessage = error.message || 'Failed to fetch control queries';
        
        // Specific handling for auth errors
        if (response.status === 401) {
          errorMessage = 'Authentication required to access control queries';
        } else if (response.status === 403) {
          errorMessage = 'You do not have permission to control this execution';
        }
        
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).status = response.status;
        (enhancedError as any).executionId = executionId;
        (enhancedError as any).timestamp = new Date().toISOString();
        (enhancedError as any).isAuthError = response.status === 401 || response.status === 403;
        
        throw enhancedError;
      }

      return response.json();
    },
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 60 * 1000, // 1 minute
    refetchInterval: (query) => {
      // Optimize polling based on WebSocket health and execution state
      if (!websocketHealthy.current) {
        // Poll aggressively when WebSocket is down
        return query.state.data?.currentStage ? 2000 : 10000; // 2s active, 10s idle
      }
      
      // Normal polling when WebSocket is healthy
      if (query.state.data?.currentStage) {
        return 15000; // 15 seconds (less aggressive when WS is working)
      }
      return 60000; // 1 minute when idle and WS is healthy
    },
    enabled: !!executionId,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry up to 3 times for 5xx errors or network issues
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Subscribe to WebSocket updates with health monitoring
  useEffect(() => {
    if (!executionId) return;

    const websocketClient = getWebSocketClient();
    if (!websocketClient) {
      websocketHealthy.current = false;
      return;
    }

    // Track WebSocket health
    websocketHealthy.current = true;

    const unsubscribeFunctions: (() => void)[] = [];

    // Listen for queries updates
    const unsubscribeQueriesUpdated = websocketClient.on('control:queries_updated', (data: any) => {
      if (data.executionId === executionId) {
        queryClient.setQueryData(controlKeys.queries(executionId), data.queries);
      }
    });

    // Listen for signal processing (may affect state)
    const unsubscribeSignalProcessed = websocketClient.on('control:signal_processed', (data: any) => {
      if (data.executionId === executionId) {
        // Invalidate queries to refetch latest state
        queryClient.invalidateQueries({
          queryKey: controlKeys.queries(executionId)
        });
      }
    });

    // Listen for execution events that affect control state
    const unsubscribeStageCompleted = websocketClient.on('execution:stage_completed', (data) => {
      if (data.execution_id === executionId) {
        queryClient.invalidateQueries({
          queryKey: controlKeys.queries(executionId)
        });
      }
    });

    const unsubscribePaused = websocketClient.on('execution:paused', (data) => {
      if (data.execution_id === executionId) {
        queryClient.invalidateQueries({
          queryKey: controlKeys.queries(executionId)
        });
      }
    });

    const unsubscribeResumed = websocketClient.on('execution:resumed', (data) => {
      if (data.execution_id === executionId) {
        queryClient.invalidateQueries({
          queryKey: controlKeys.queries(executionId)
        });
      }
    });

    const unsubscribeCancelled = websocketClient.on('execution:cancelled', (data) => {
      if (data.execution_id === executionId) {
        queryClient.invalidateQueries({
          queryKey: controlKeys.queries(executionId)
        });
      }
    });

    unsubscribeFunctions.push(
      ...[
        unsubscribeQueriesUpdated,
        unsubscribeSignalProcessed,
        unsubscribeStageCompleted,
        unsubscribePaused,
        unsubscribeResumed,
        unsubscribeCancelled
      ].filter(fn => typeof fn === 'function')
    );

    // Monitor WebSocket health for optimization
    const healthCheckInterval = setInterval(() => {
      if (websocketClient && typeof websocketClient.getConnectionStatus === 'function') {
        const isHealthy = websocketClient.getConnectionStatus();
        if (websocketHealthy.current !== isHealthy) {
          websocketHealthy.current = isHealthy;
          // Trigger refetch interval recalculation when health changes
          queryClient.invalidateQueries({
            queryKey: controlKeys.queries(executionId)
          });
        }
      }
    }, 5000); // Check every 5 seconds

    return () => {
      unsubscribeFunctions.forEach(fn => fn());
      clearInterval(healthCheckInterval);
      websocketHealthy.current = false;
    };
  }, [executionId, queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    // Computed values
    isRunning: query.data?.currentStage !== null,
    isPaused: false, // Will be determined by execution status
    canControl: !!query.data, // Has data loaded
  };
}